import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {fetchIcrCoverage, recomputeIcrCoverage} from '../../../firebase/firestore/icr';
import {COVERAGE_POLL_MS, shouldPollIcrCoverage} from './coveragePolling';
import {fetchWithRetries} from './fetchWithRetries';

export {COVERAGE_POLL_MS} from './coveragePolling';

const AWAITING_TIMEOUT_MS = 5 * 60 * 1000;
/** Before the job marks computing=true, keep a brief local “starting” state. */
const STARTING_GRACE_MS = 15_000;

function createPending(scope, data) {
    const wantAnalytic = scope === 'all' || scope === 'analytic' || scope === 'ars';
    const wantCollection = scope === 'all' || scope === 'collection';
    return {
        scope,
        startedAt: Date.now(),
        analytic: wantAnalytic
            ? {
                baselineId: data?.analyticCoverage?.snapshotId ?? data?.snapshotId ?? null,
                sawComputing: false,
                done: false,
            }
            : null,
        collection: wantCollection
            ? {
                baselineId: data?.collectionCoverage?.snapshotId ?? null,
                sawComputing: false,
                done: false,
            }
            : null,
    };
}

function isSideComputing(fresh, side) {
    if (!fresh) {
        return false;
    }
    if (side === 'analytic') {
        return Boolean(fresh.analyticCoverage?.computing ?? fresh.computing);
    }
    return Boolean(fresh.collectionCoverage?.computing);
}

function sideSnapshotId(fresh, side) {
    if (!fresh) {
        return null;
    }
    if (side === 'analytic') {
        return fresh.analyticCoverage?.snapshotId ?? fresh.snapshotId ?? null;
    }
    return fresh.collectionCoverage?.snapshotId ?? null;
}

/**
 * Advance per-side awaiting. Clears a side when the server reports not-computing
 * after we saw computing, or when the snapshot id changed and computing is false,
 * or on timeout. Does not require snapshot-id change alone (fixes stuck spinners).
 */
export function resolveAwaitingProgress(pending, fresh) {
    if (!pending) {
        return {pending: null, awaitingAnalytic: false, awaitingCollection: false};
    }
    const timedOut = Date.now() - pending.startedAt > AWAITING_TIMEOUT_MS;
    const next = {
        ...pending,
        analytic: pending.analytic ? {...pending.analytic} : null,
        collection: pending.collection ? {...pending.collection} : null,
    };

    for (const side of ['analytic', 'collection']) {
        const track = next[side];
        if (!track || track.done) {
            continue;
        }
        const computing = isSideComputing(fresh, side);
        if (computing) {
            track.sawComputing = true;
            continue;
        }
        const id = sideSnapshotId(fresh, side);
        const changed = Boolean(id) && id !== track.baselineId;
        const startingExpired = Date.now() - pending.startedAt > STARTING_GRACE_MS;
        // Clear when the run finished (saw computing → not computing), snapshot rotated,
        // or the job never advertised computing within the starting grace window.
        if (track.sawComputing || changed || timedOut || startingExpired) {
            track.done = true;
        }
    }

    const awaitingAnalytic = Boolean(next.analytic && !next.analytic.done);
    const awaitingCollection = Boolean(next.collection && !next.collection.done);
    if (!awaitingAnalytic && !awaitingCollection) {
        return {pending: null, awaitingAnalytic: false, awaitingCollection: false};
    }
    return {pending: next, awaitingAnalytic, awaitingCollection};
}

/**
 * While a side is awaiting a local recompute enqueue, set computing=true so the
 * panel/modal/tab stay in sync until the server flips its flag. Passive poll
 * must never call this — only recompute().
 */
function withLocalStartingFlags(fresh, awaitingAnalytic, awaitingCollection) {
    const startingAnalytic = {
        progressLabel: 'Step 0: AR extraction',
        progressStepNumber: 0,
        progressStepTitle: 'AR extraction',
        progressDetail: 'Starting…',
    };
    const startingCollection = {
        progressLabel: 'Step 0: AR extraction',
        progressStepNumber: 0,
        progressStepTitle: 'AR extraction',
        progressDetail: 'Starting…',
    };
    if (!fresh) {
        return {
            computing: awaitingAnalytic,
            ...(awaitingAnalytic
                ? {
                    ...startingAnalytic,
                    analyticCoverage: {computing: true, ...startingAnalytic},
                }
                : {}),
            collectionCoverage: awaitingCollection
                ? {computing: true, ...startingCollection}
                : undefined,
        };
    }
    const next = {...fresh};
    const analyticAlready = Boolean(fresh.analyticCoverage?.computing ?? fresh.computing);
    if (awaitingAnalytic && !analyticAlready) {
        next.computing = true;
        next.analyticCoverage = {
            ...(fresh.analyticCoverage || {}),
            computing: true,
            ...(fresh.analyticCoverage?.progressLabel || fresh.progressLabel
                ? {}
                : startingAnalytic),
        };
        if (!fresh.progressLabel) {
            Object.assign(next, startingAnalytic);
        }
    }
    if (awaitingCollection && !fresh.collectionCoverage?.computing) {
        next.collectionCoverage = {
            ...(fresh.collectionCoverage || {}),
            computing: true,
            ...(fresh.collectionCoverage?.progressLabel ? {} : startingCollection),
        };
    }
    return next;
}

/**
 * Load + poll ICR coverage; enqueue recompute via POST /api/icrs/{id}/coverage/recompute.
 * @param {string|null} icrId
 */
export function useIcrCoverage(icrId) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [recomputing, setRecomputing] = useState(false);
    const [awaitingAnalytic, setAwaitingAnalytic] = useState(false);
    const [awaitingCollection, setAwaitingCollection] = useState(false);
    const [error, setError] = useState(null);
    const [retryingFetch, setRetryingFetch] = useState(false);
    const awaitingRef = useRef(null);

    const applyFresh = useCallback((fresh) => {
        const resolved = resolveAwaitingProgress(awaitingRef.current, fresh);
        awaitingRef.current = resolved.pending;
        setAwaitingAnalytic(resolved.awaitingAnalytic);
        setAwaitingCollection(resolved.awaitingCollection);
        // Passive load / poll: trust server computing flags only (no optimistic inject).
        setData(fresh);
        return fresh;
    }, []);

    const reload = useCallback(() => {
        if (!icrId) {
            setData(null);
            return Promise.resolve(null);
        }
        setLoading(true);
        setError(null);
        setRetryingFetch(true);
        return fetchWithRetries(() => fetchIcrCoverage(icrId))
            .then((fresh) => {
                setError(null);
                return applyFresh(fresh);
            })
            .catch((err) => {
                setError(err);
                return null;
            })
            .finally(() => {
                setLoading(false);
                setRetryingFetch(false);
            });
    }, [icrId, applyFresh]);

    const recompute = useCallback(async (options = {}) => {
        const scope = options.scope || 'all';
        const sideBusy = (scope === 'collection' && awaitingCollection)
            || (scope === 'analytic' && awaitingAnalytic)
            || (scope === 'ars' && awaitingAnalytic)
            || (scope === 'all' && (awaitingAnalytic || awaitingCollection));
        if (!icrId || recomputing || sideBusy) {
            return null;
        }
        setRecomputing(true);
        setError(null);
        const pending = createPending(scope, data);
        awaitingRef.current = pending;
        setAwaitingAnalytic(Boolean(pending.analytic));
        setAwaitingCollection(Boolean(pending.collection));
        // Local starting flags only after an explicit recompute enqueue.
        setData((prev) => withLocalStartingFlags(
            prev,
            Boolean(pending.analytic),
            Boolean(pending.collection),
        ));
        try {
            await recomputeIcrCoverage(icrId, options);
            await reload();
            return null;
        } catch (err) {
            awaitingRef.current = null;
            setAwaitingAnalytic(false);
            setAwaitingCollection(false);
            setError(err);
            await reload();
            return null;
        } finally {
            setRecomputing(false);
        }
    }, [icrId, recomputing, awaitingAnalytic, awaitingCollection, data, reload]);

    useEffect(() => {
        awaitingRef.current = null;
        setAwaitingAnalytic(false);
        setAwaitingCollection(false);
        reload();
    }, [reload]);

    const awaitingRecompute = awaitingAnalytic || awaitingCollection;

    const pollCoverage = useMemo(
        () => !recomputing && (awaitingRecompute || shouldPollIcrCoverage(data)),
        [data, recomputing, awaitingRecompute],
    );

    useEffect(() => {
        if (!pollCoverage) {
            return undefined;
        }
        const timer = setInterval(() => {
            reload();
        }, COVERAGE_POLL_MS);
        return () => clearInterval(timer);
    }, [pollCoverage, reload]);

    return {
        data,
        loading,
        error,
        reload,
        recompute,
        recomputing,
        retryingFetch,
        awaitingRecompute,
        awaitingAnalytic,
        awaitingCollection,
        /** @deprecated use awaitingAnalytic / awaitingCollection */
        awaitingScope: awaitingRecompute
            ? (awaitingAnalytic && awaitingCollection
                ? 'all'
                : (awaitingCollection ? 'collection' : 'analytic'))
            : null,
    };
}
